import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { titleCaseSentence, upperSnakeCase } from '@/utils';
import { POI_DETAILS_DEFAULT, POI_DETAILS_FALLBACK } from '@/constants';
import { LegendColors } from '@/types';
import { KeyValueMap, mapFindingsData } from './../utils';
import {
  PointOfInterestsFindingsByDeckAndPaintBlock,
  useGetAllPointOfInterestTemplatesQuery,
} from '@/__generated__/graphql';

type FindingsChartData = {
  findingsCountByDeck: KeyValueMap[];
  findingsCountByPaintBlock: KeyValueMap[];
};

export const useFindingsData = (
  findingsByPaintBlocksAndDecks: PointOfInterestsFindingsByDeckAndPaintBlock | undefined,
  selectedPOIType: string[] = [],
  activeStructureIds: string[] = []
) => {
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);

  const { data: fetchedTemplates } = useGetAllPointOfInterestTemplatesQuery({
    variables: {
      structureId: activeStructureIds[0],
    },
    fetchPolicy: 'cache-first',
    skip:
      !activeStructureIds[0] ||
      (allPointOfInterestTemplates.length > 0 && activeStructureIds.length > 0),
  });

  const pointOfInterestTemplates = useMemo(
    () =>
      allPointOfInterestTemplates.length > 0
        ? allPointOfInterestTemplates
        : fetchedTemplates?.allPointOfInterestTemplate || [],
    [allPointOfInterestTemplates, fetchedTemplates]
  );

  const updateFindings = useCallback(
    (data: KeyValueMap[]) => {
      return data.map((poiData) => {
        Object.keys(poiData).forEach((key) => {
          if (
            key !== 'name' &&
            !selectedPOIType.some((poi) => poi.toLowerCase().includes(key.toLowerCase()))
          ) {
            poiData[key] = 0;
          }
        });
        return poiData;
      });
    },
    [selectedPOIType]
  );

  const chartsData: FindingsChartData = useMemo(() => {
    if (selectedPOIType.length === 0 && findingsByPaintBlocksAndDecks) {
      return { ...mapFindingsData({ ...findingsByPaintBlocksAndDecks }) };
    } else if (findingsByPaintBlocksAndDecks) {
      const mappedDataClone = { ...mapFindingsData({ ...findingsByPaintBlocksAndDecks }) };
      return {
        findingsCountByDeck: updateFindings(mappedDataClone.findingsCountByDeck),
        findingsCountByPaintBlock: updateFindings(mappedDataClone.findingsCountByPaintBlock),
      };
    }

    return { findingsCountByDeck: [], findingsCountByPaintBlock: [] };
  }, [findingsByPaintBlocksAndDecks, selectedPOIType.length, updateFindings]);

  const findingsLegend = useMemo(() => {
    return pointOfInterestTemplates.reduce((accumulator: LegendColors, current) => {
      const name = titleCaseSentence(current.name);
      const templateColor = pointOfInterestTemplates.find(
        (template) => upperSnakeCase(template.name) === upperSnakeCase(name)
      )?.accentColor;
      accumulator[name] =
        templateColor ??
        POI_DETAILS_FALLBACK.find((fallbackDetail) => fallbackDetail.name === upperSnakeCase(name))
          ?.accentColor ??
        POI_DETAILS_DEFAULT.accentColor;
      return accumulator;
    }, {});
  }, [pointOfInterestTemplates]);

  return {
    chartsData,
    findingsLegend,
    pointOfInterestTemplates,
  };
};
